<template>
  <section class="shape-picker" :class="[`shape-picker--${variant}`, { 'shape-picker--admin': userStore.isAdmin }]" :aria-labelledby="headingId">
    <header>
      <button type="button" class="shape-picker__toggle" :aria-expanded="!collapsedSections.has(primarySectionId)" :aria-controls="`${headingId}-content`" @click="toggleSection(primarySectionId)">
        <h2 :id="headingId">{{ heading }}</h2><svg class="shape-picker__chevron" :class="{ 'shape-picker__chevron--collapsed': collapsedSections.has(primarySectionId) }" width="20" height="20" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </header>
    <template v-for="group in visibleGroups" :key="group.id">
      <template v-if="variant !== '3d' && group.id === '3d-blocks'">
        <NewStudioRightPanelThreeDBoxesBlank :unavailable="true" />
      </template>
      <template v-else>
        <div v-if="(groupFilter === 'all' || groupFilter === 'other') && group.id !== primarySectionId && groupHeading(group)" class="shape-picker__group-heading">
          <button type="button" class="shape-picker__toggle" :aria-expanded="!collapsedSections.has(sectionForGroup(group.id))" @click="toggleSection(sectionForGroup(group.id))">
            <h2>{{ groupHeading(group) }}</h2>
            <svg class="shape-picker__chevron" :class="{ 'shape-picker__chevron--collapsed': collapsedSections.has(sectionForGroup(group.id)) }" width="20" height="20" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
        <p v-if="group.label" v-show="!collapsedSections.has(sectionForGroup(group.id))" class="shape-picker__other-label">
          <template v-if="group.label.includes('3D Grid')">
            Shapes available in <button type="button" class="shape-picker__grid-link" aria-haspopup="dialog"
              @mouseenter="highlightAvailableGrids([GRID_PATTERN_IDS.THREE_D])"
              @click="gridOptions([GRID_PATTERN_IDS.THREE_D])">3D Grid</button>
          </template>
          <template v-else-if="group.label === 'Shapes available on other grids'">
            Shapes available on other <button type="button" class="shape-picker__grid-link" aria-haspopup="dialog"
              @mouseenter="highlightAvailableGrids(getAvailableGridIds(group.rows.flat().map(shape => shape.id), patternId))"
              @click="gridOptions(getAvailableGridIds(group.rows.flat().map(shape => shape.id), patternId))">grids</button>
          </template>
          <template v-else>{{ group.label }}</template>
        </p>
        <div
          v-show="!collapsedSections.has(sectionForGroup(group.id))"
          :id="group.id === primarySectionId ? `${headingId}-content` : undefined"
          class="shape-picker__group"
          :class="{ 'shape-picker__grid--unavailable': group.unavailable }"
          :data-shape-slot="group.id"
          :data-shape-group="group.id"
          :style="getPickerGroupStyle(variant, group.id)"
          @click="interactions.closeShapeGroup"
        >
          <template v-for="(sectionRows, sectionIndex) in (group.id === 'engraved-tiles' && !group.unavailable ? [group.rows.slice(0, 1), group.rows.slice(1)] : [group.rows])" :key="sectionIndex">
        <div v-if="sectionIndex === 1" class="shape-picker__engraved-depth">
          <p>Change the Depth of the Engraved Tiles</p>
          <div role="group" aria-label="Engraved tile depth">
            <button v-for="depth in [9, 18]" :key="depth" type="button" :aria-pressed="engravedDepth === depth" :class="{ active: engravedDepth === depth }" @click.stop="setEngravedDepth(depth)"><span>{{ depth }}mm</span><img :src="`/new-studio/shapes/layered-engraved/depth-${engravedDepth === depth ? 'active' : 'inactive'}.svg`" alt="" /></button>
          </div>
        </div>
        <div class="shape-picker__section" :class="{ 'shape-picker__engraved-panel': sectionIndex === 1, 'shape-picker__layered-panel': sectionIndex === 1 && engravedDepth === 18 }">
        <p v-if="sectionIndex === 1 && engravedDepth === 18" class="shape-picker__layered-guidance">Pick up to 2 Colors for the Bottom and Top Layers of Felt</p>
        <template v-for="(row, rowIndex) in sectionRows" :key="rowIndex">
        <p v-if="group.rowLabels?.[rowIndex + sectionIndex]" class="shape-picker__row-label">{{ group.rowLabels[rowIndex + sectionIndex] }}</p>
        <div class="shape-picker__grid" :style="getPickerRowStyle(variant, group.id, rowIndex + sectionIndex)" data-shape-row>
        <template v-for="shape in row" :key="shape.id">
          <div class="shape-picker__item" :class="{ 'shape-picker__item--unavailable': isShapeUnavailable(group, shape.id) }">
            <button
              type="button"
              data-shape-root
              :data-shape-id="shape.id"
              :disabled="isShapeUnavailable(group, shape.id)"
              :aria-disabled="isShapeUnavailable(group, shape.id) ? 'true' : undefined"
              :class="{
                active: gridStore.selectedShape?.id === shape.id || shape.children?.some((child) => child.id === gridStore.selectedShape?.id),
              }"
              :data-block-depth="getBlockDepthLabel(shape.id)"
              :style="buttonStyle(shape.id)"
              :aria-pressed="Boolean(gridStore.selectedShape?.id === shape.id || shape.children?.some((child) => child.id === gridStore.selectedShape?.id))"
              :aria-label="shape.label"
              :title="shape.label"
              @click.stop="picker.selectShape(shape, $event)"
            >
              <NewStudioLayeredPickerIcon v-if="isLayeredShape(shape)" :pattern="shape.prefix.replace('Layered', '')" class="shape-picker__layered-icon" />
              <img v-else-if="group.id === 'engraved-tiles' && sectionIndex === 1" :src="`/new-studio/shapes/engraved-9mm/${shape.prefix}.svg`" alt="" class="shape-picker__engraved-9mm" />
              <ShapesWoodSlatPickerIcon v-else-if="isWoodSlatShapeId(shape.id)" :selected="gridStore.selectedShape?.id === shape.id" :disabled="isShapeUnavailable(group, shape.id)" :style="shapeSVGStyle(shape)" />
              <component v-else :is="shapeComponents[shape.id]" :style="shapeSVGStyle(shape)" :class="getSVGClass(shape)" />
              <span v-if="getBlockDepthLabel(shape.id)" class="shape-picker__block-label">{{ getBlockDepthLabel(shape.id) }}</span>
            </button>
            <div v-if="shape.children?.length && interactions.activeShapeGroup.value?.id === shape.id" class="shape-picker__children" :class="{ 'shape-picker__children--right': getMenuAlign(group, shape, row) === 'right' }" :style="getPickerChildrenStyle(variant)">
              <button
                type="button"
                :data-shape-menu-id="shape.id"
                :disabled="isShapeUnavailable(group, shape.id)"
                :aria-disabled="isShapeUnavailable(group, shape.id) ? 'true' : undefined"
                :class="{ active: gridStore.selectedShape?.id === shape.id }"
                :style="buttonStyle(shape.id)"
                :aria-pressed="gridStore.selectedShape?.id === shape.id"
                :aria-label="shape.label"
                :title="shape.label"
                @click.stop="selectMenuParent(shape, $event)"
              >
                <NewStudioLayeredPickerIcon v-if="isLayeredShape(shape)" :pattern="shape.prefix.replace('Layered', '')" class="shape-picker__layered-icon" />
              <img v-else-if="group.id === 'engraved-tiles' && sectionIndex === 1" :src="`/new-studio/shapes/engraved-9mm/${shape.prefix}.svg`" alt="" class="shape-picker__engraved-9mm" />
              <ShapesWoodSlatPickerIcon v-else-if="isWoodSlatShapeId(shape.id)" :selected="gridStore.selectedShape?.id === shape.id" :disabled="isShapeUnavailable(group, shape.id)" :style="shapeSVGStyle(shape)" />
                <component v-else :is="shapeComponents[shape.id]" :style="shapeSVGStyle(shape)" :class="getSVGClass(shape)" />
              </button>
              <button
                v-for="child in shape.children"
                :key="child.id"
                :data-shape-id="child.id"
                type="button"
                :disabled="isShapeUnavailable(group, shape.id)"
                :aria-disabled="isShapeUnavailable(group, shape.id) ? 'true' : undefined"
                :class="{ active: gridStore.selectedShape?.id === child.id }"
                :aria-pressed="gridStore.selectedShape?.id === child.id"
                :aria-label="child.label"
                :title="child.label"
                :style="buttonStyle(child.id, shape.id)"
                @click.stop="selectChild(child, $event)"
              >
                <NewStudioLayeredPickerIcon v-if="isLayeredShape(child)" :pattern="child.prefix.replace('Layered', '')" class="shape-picker__layered-icon" :style="{ transform: `rotate(${(child.rotation || 0) + (variant === '45-square' ? 45 : 0)}deg) !important` }" />
                <img v-else-if="group.id === 'engraved-tiles' && sectionIndex === 1" :src="`/new-studio/shapes/engraved-9mm/${child.prefix}.svg`" alt="" class="shape-picker__engraved-9mm" :style="{ transform: `rotate(${child.rotation || 0}deg) !important` }" />
                <ShapesWoodSlatPickerIcon v-else-if="isWoodSlatShapeId(child.id)" :selected="gridStore.selectedShape?.id === child.id" :disabled="isShapeUnavailable(group, shape.id)" :style="shapeSVGStyle(child, shape.id)" />
                <component v-else :is="shapeComponents[child.id]" :style="shapeSVGStyle(child, shape.id)" :class="getSVGClass(child)" />
              </button>
            </div>
          </div>
        </template>
        </div>
        </template>
        </div>
        </template>
      </div>
      </template>
    </template>
  </section>
</template>

<script setup lang="ts">
import { LAYERED_ENGRAVED_SHAPES } from '~/composables/shapes/layeredEngraving'
import { getColorVariation } from '~/composables/shapes/variationMatching'
import { isLayeredShape } from '~/shared/layeredEngraving'
import { GRID_PATTERN_IDS, SELECTABLE_TOOLS } from '~/composables/grid/constants'
import { GRID_SHAPES } from '~/composables/shapes/constants'
import { isWoodSlatShapeId } from '~/composables/shapes/woodSlats'
import { getSVGClass, getSVGStyle } from '~/composables/useShapeIconStyle'
import type { Shape } from '~/types/grid.types'
import { buildPatternShapeCatalog, createPickerInteractions } from './logic'
import { getBlockDepthLabel, getPickerButtonSize, getPickerChildrenStyle, getPickerGroupStyle, getPickerIconStyle, getPickerRowStyle, getPickerStrokeWidth, getPickerTransform } from './right-panel/shapeLayout'
import { getAvailableGridIds } from './right-panel/gridAvailability'
import { PANEL_SHAPE_GROUPS } from './right-panel/panelContract'
import type { PanelVariantName, ShapeGroupTreatment } from './right-panel/variantMap'

const props = withDefaults(defineProps<{
  variant?: PanelVariantName
  groupTreatment?: ShapeGroupTreatment
  groupFilter?: 'all' | 'shapes' | 'engraved' | 'other'
  heading?: string
}>(), { variant: 'square', groupTreatment: 'available-and-other', groupFilter: 'shapes', heading: 'SHAPES' })

const gridStore = useGridStore()
const userStore = useUserStore()
const { gridOptions, highlightAvailableGrids } = useNewStudioActions()
const { fetchConfig, getShapeFlags } = useStudioConfig()
const interactions = useShapeInteractions()
const picker = createPickerInteractions(interactions, () => gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD))
const collapsedSections = ref(new Set<string>())
const primarySectionId = computed(() => {
  if (props.groupFilter === 'other') return 'shapes'
  if (props.variant === '3d') return '3d-blocks'
  if (props.groupFilter === 'engraved') return 'engraved-tiles'
  return 'shapes'
})
const sectionForGroup = (groupId: string) => groupId === 'other-grids' ? 'shapes' : groupId
const groupHeading = (group: { id: string, heading?: string }) => group.heading
  || (group.id === 'engraved-tiles' ? 'ENGRAVED TILES'
    : group.id === 'printed-tiles' ? 'PRINTED TILES'
      : group.id === 'wood-slats' ? 'WOOD SLATS'
      : group.id === '3d-blocks' ? '3D BOXES - BLANK'
        : group.id === '3d-engraved' ? '3D BOXES - ENGRAVED' : '')
const toggleSection = (sectionId: string) => {
  const next = new Set(collapsedSections.value)
  next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId)
  collapsedSections.value = next
}
const patternId = computed(() => (gridStore.gridPattern?.id || 'square') as keyof typeof GRID_SHAPES)
const catalog = computed(() => buildPatternShapeCatalog(patternId.value, GRID_SHAPES))
const contractGroups = computed(() => PANEL_SHAPE_GROUPS[props.variant].filter((group) => {
  if (props.groupFilter === 'all') return group.id !== '3d-engraved'
  if (props.groupFilter === 'engraved') return group.id === 'engraved-tiles'
  if (props.groupFilter === 'other') return group.id !== '3d-blocks' && group.id !== '3d-engraved'
  return group.id !== 'engraved-tiles' && group.id !== 'printed-tiles' && group.id !== '3d-engraved'
}))
const engravedDepth = ref(9)
const setEngravedDepth = (depth: number) => {
  engravedDepth.value = depth
  interactions.closeShapeGroup()
  gridStore.unselectItemsOnGrid()
  gridStore.setTempNewItems([])
  gridStore.selectedShape = null
}
watch(() => gridStore.selectedShape, shape => {
  if (isLayeredShape(shape)) engravedDepth.value = 18
  else if (shape?.id.startsWith('Engraved')) engravedDepth.value = 9
})
const visibleGroups = computed(() => contractGroups.value.map(group => {
  if (group.id === 'engraved-tiles' && !group.unavailable && engravedDepth.value === 18) {
    const is45Square = props.variant === '45-square'
    return { ...group, rows: [
      (group.rows?.[0] || []).map(id => catalog.value[id]).filter(Boolean),
      is45Square ? LAYERED_ENGRAVED_SHAPES.slice(0, 3) : LAYERED_ENGRAVED_SHAPES.slice(0, 4),
      is45Square ? LAYERED_ENGRAVED_SHAPES.slice(3, 6) : LAYERED_ENGRAVED_SHAPES.slice(4),
      is45Square ? LAYERED_ENGRAVED_SHAPES.slice(6) : null,
    ].filter(Boolean) }
  }
  // 3d-engraved uses static preview rows — skip catalog filtering
  if (group.id === '3d-engraved' && group.unavailable) {
    return { ...group, rows: (group.rows || []).map(row => row.map(id => catalog.value[id]).filter(Boolean)) }
  }
  return {
    ...group,
    rows: (group.rows || [group.shapeIds]).map(row => row.map(id => catalog.value[id]).filter((shape) => {
      if (!shape) return false
      if (isWoodSlatShapeId(shape.id) && !userStore.isAdmin) return false
      const flags = getShapeFlags(shape.id, shape.admin, shape.active)
      return flags.active && (!flags.admin || userStore.isAdmin || group.unavailable)
    })).filter(row => row.length > 0),
  }
}).filter(group => group.rows.length > 0))
const isShapeUnavailable = (group: { unavailable: boolean, unavailableShapeIds?: string[] }, shapeId: string) =>
  group.unavailable || (group.unavailableShapeIds?.includes(shapeId) ?? false)
  || (shapeId.startsWith('LayeredEngraved') && !getColorVariation(LAYERED_ENGRAVED_SHAPES.find(s => s.id === shapeId)!, { label: 'Ebony', image: '' }, { allowFallback: false }))

// Open the children dropdown to the left of the parent when the parent is
// the rightmost tile in its row, so the dropdown stays inside the panel
// instead of overflowing the right edge.
const getMenuAlign = (group: { rows: Shape[][] }, shape: Shape, row: Shape[]): 'right' | undefined => {
  if (shape.menuAlign === 'right') return 'right'
  if (row.indexOf(shape) === row.length - 1) return 'right'
  return undefined
}
const shapes = computed(() => visibleGroups.value.flatMap(group => group.rows.flat())
  .flatMap(shape => [shape, ...(shape.children || [])]))
const compactEngravedIds = new Set(['EngravedBackSlash', 'EngravedCross', 'EngravedFourSquare', 'EngravedParallel', 'EngravedBackSlash45deg', 'EngravedCross45deg', 'EngravedFourSquare45deg', 'EngravedParallel45deg', 'EngravedBackSlashFreeform', 'EngravedCrossFreeform', 'EngravedFourSquareFreeform', 'EngravedParallelFreeform'])
const buttonStyle = (shapeId: string, familyId?: string) => {
  if (shapeId.startsWith('LayeredEngraved')) return { width: '58px', height: '58px' }
  const { width, height } = getPickerButtonSize(props.variant, shapeId)
  // Engraved children in dropdown: scale down only when parent is a compact (1x1) engraved tile
  if (familyId && shapeId.startsWith('Engraved') && compactEngravedIds.has(familyId)) {
    return { width: `${width * 0.7}px`, height: `${height * 0.7}px` }
  }
  return { width: `${width}px`, height: `${height}px` }
}
const selectMenuParent = (shape: Shape, event: MouseEvent) => {
  picker.releasePointerFocus(event)
  if (gridStore.selectedShape?.id === shape.id) {
    interactions.closeShapeGroup()
    return
  }
  interactions.handleShapeClick(shape)
  gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD)
}
const selectChild = (shape: Shape, event: MouseEvent) => {
  picker.releasePointerFocus(event)
  interactions.handleShapeClick(shape)
  gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD)
  interactions.closeShapeGroup()
}
const displayShape = (shape: Shape) => shape.children?.find(child => child.id === gridStore.selectedShape?.id) || shape
function shapeSVGStyle(shape: Shape, familyId?: string) {
  const style = getSVGStyle(shape)
  const finalTransform = getPickerTransform(shape.id, style.transform)
  // Engraved children in dropdown: scale down only when parent is a compact (1x1) engraved tile
  if (familyId && shape.id.startsWith('Engraved') && compactEngravedIds.has(familyId)) {
    const layoutId = shape.id
    const iconStyle = getPickerIconStyle(props.variant, layoutId, finalTransform)
    const w = parseFloat(iconStyle.width) * 0.7
    const h = parseFloat(iconStyle.height) * 0.7
    return {
      ...style,
      ...iconStyle,
      width: `${w}px`,
      height: `${h}px`,
      '--shape-stroke': getPickerStrokeWidth(iconStyle.transform),
      marginBottom: '0',
    }
  }
  const layoutId = shape.id
  const iconStyle = getPickerIconStyle(props.variant, layoutId, finalTransform)
  return {
    ...style,
    ...iconStyle,
    '--shape-stroke': getPickerStrokeWidth(iconStyle.transform),
    marginBottom: '0',
  }
}
const headingId = computed(() => `${props.variant}-${props.heading.toLowerCase().replace(/\s+/g, '-')}-heading`)
const svgModules = import.meta.glob('../Shapes/SVG/**/*.svg', { eager: true, query: '?component', import: 'default' })
const blockPreviewRows = [
  [
    { id: 'BlockBlank4', label: '36"×36"×4" Blank', depth: '4ʺ', width: 82, height: 82, svg: '/new-studio/shapes/3d/Cube36x36x4.svg' },
    { id: 'BlockRect4', label: '12"×36"×4" Blank', depth: '4ʺ', width: 32, height: 82, svg: '/new-studio/shapes/3d/Rect12x36x4.svg' },
    { id: 'BlockSmallBlank4', label: '12"×12"×4" Blank', depth: '4ʺ', width: 32, height: 32, svg: '/new-studio/shapes/3d/Cube12x12x4.svg' },
  ],
]
const engravedBlockPreviewRows = [
  [
    { id: 'EngBlock-Parallel', label: 'Large Parallel', svg: '/new-studio/shapes/3d-engraved/LargeParallel4.svg' },
    { id: 'EngBlock-Tangent', label: 'Tangent', svg: '/new-studio/shapes/3d-engraved/Tangent4.svg' },
    { id: 'EngBlock-Zen', label: 'Zen', svg: '/new-studio/shapes/3d-engraved/Zen4.svg' },
  ],
  [
    { id: 'EngBlock-River', label: 'River', svg: '/new-studio/shapes/3d-engraved/River4.svg' },
    { id: 'EngBlock-Offset', label: 'Offset', svg: '/new-studio/shapes/3d-engraved/Offset4.svg' },
    { id: 'EngBlock-Topo', label: 'Topo', svg: '/new-studio/shapes/3d-engraved/Topo4.svg' },
  ],
  [
    { id: 'EngBlock-Converge', label: 'Converge', svg: '/new-studio/shapes/3d-engraved/Converge4.svg' },
    { id: 'EngBlock-Subway', label: 'Subway', svg: '/new-studio/shapes/3d-engraved/Subway4.svg' },
  ],
]
const previewComponents = Object.fromEntries(
  [...new Set(blockPreviewRows.flatMap(row => row.map(item => item.icon).filter(Boolean)))]
    .map(icon => [icon, svgModules[`../Shapes/SVG/square/${icon}.svg`]]),
)
const shapeComponents = computed(() => Object.fromEntries(shapes.value.map((shape) => [
  shape.id,
  svgModules[`../Shapes/SVG/${patternId.value}/${shape.icon}.svg`]
    || svgModules[`../Shapes/SVG/hex/${shape.icon}.svg`]
    || svgModules[`../Shapes/SVG/3d/${shape.icon}.svg`]
    || svgModules[`../Shapes/SVG/square/${shape.icon}.svg`],
])))
onMounted(fetchConfig)
watch(() => props.variant, interactions.closeShapeGroup)
</script>

<style scoped>
.shape-picker { margin-top: 36px; margin-right: -16px; padding-bottom: 20px; overflow: visible; }
.shape-picker--square { margin-top: 41px; }
header { margin-bottom: 16px; }
.shape-picker__toggle { display: flex; width: 100%; padding: 0; align-items: center; justify-content: space-between; border: 0; color: inherit; background: transparent; cursor: pointer; }
.shape-picker--square > header { position: relative; top: -6px; height: 18px; margin-bottom: 14px; }
h2 { margin: 0; color: #95948f; font-size: 13px; font-weight: 800; letter-spacing: .08em; }
.shape-picker__chevron { flex-shrink: 0; width: 20px; height: 20px; color: #5f5e59; margin-right: 8px; transition: transform 0.2s ease; }
.shape-picker__chevron--collapsed { transform: rotate(-90deg); }
.shape-picker__group { display: flex; flex-direction: column; gap: 18px; }
.shape-picker__grid { display: flex; min-height: 24px; flex-wrap: wrap; align-items: flex-end; gap: 18px 12px; }
.shape-picker--45-square > header { margin-bottom: 19px; }
.shape-picker--45-square .shape-picker__group, .shape-picker--freeform .shape-picker__group { gap: 20px; }
.shape-picker--45-square .shape-picker__grid, .shape-picker--freeform .shape-picker__grid { row-gap: 0; }
.shape-picker--45-square .shape-picker__grid { column-gap: 10px; }
.shape-picker__group-heading { display: flex; align-items: center; justify-content: space-between; margin: 20px 0; }
.shape-picker--square > .shape-picker__group-heading { height: 18px; margin-bottom: 18px; }
.shape-picker--square [data-shape-group="other-grids"] + .shape-picker__group-heading { position: relative; top: -2px; margin-top: 22px; }
.shape-picker--square [data-shape-group="engraved-tiles"] + .shape-picker__group-heading { margin-top: 12px; }
.shape-picker__item { position: relative; display: grid; flex: 0 0 auto; place-items: center; }
.shape-picker__grid button { position: relative; display: grid; box-sizing: border-box; flex: 0 0 auto; padding: 0; place-items: center; border: 0; background: transparent; cursor: pointer; }
.shape-picker__grid button.active { border-color: transparent; }
[data-block-depth] :deep(svg path:nth-child(2)) { display: none; }
.shape-picker__block-label { position: absolute; inset: 0; display: grid; place-items: center; color: #7b7a75; font-family: Montserrat, sans-serif; font-size: 13px; font-weight: 700; line-height: normal; pointer-events: none; }
.shape-picker__row-label { margin: -13px 0 -13px; color: #8f8e89; font-size: 11px; line-height: 14px; }
.shape-picker__grid button :deep(path),
.shape-picker__grid button :deep(rect),
.shape-picker__grid button :deep(polygon),
.shape-picker__grid button :deep(circle),
.shape-picker__grid button :deep(ellipse) { fill: transparent !important; stroke: #8f8e89 !important; stroke-width: var(--shape-stroke, 2.4px) !important; vector-effect: non-scaling-stroke; }
.shape-picker__grid button:not(:disabled):not(.active):hover :deep(path),
.shape-picker__grid button:not(:disabled):not(.active):hover :deep(rect),
.shape-picker__grid button:not(:disabled):not(.active):hover :deep(polygon),
.shape-picker__grid button:not(:disabled):not(.active):hover :deep(circle),
.shape-picker__grid button:not(:disabled):not(.active):hover :deep(ellipse) { fill: #dfe4e5 !important; stroke: #b8c6ce !important; }
.shape-picker__grid button.active :deep(path),
.shape-picker__grid button.active :deep(rect),
.shape-picker__grid button.active :deep(polygon),
.shape-picker__grid button.active :deep(circle),
.shape-picker__grid button.active :deep(ellipse) { fill: #ccd5da !important; stroke: #2f365d !important; }
.shape-picker__grid--unavailable button { cursor: not-allowed; opacity: 1; }
.shape-picker__grid--unavailable[data-shape-group="engraved-tiles"] button { background: #cfceca !important; }
.shape-picker__grid--unavailable[data-shape-group="printed-tiles"] button { background: #cfceca !important; }
.shape-picker__grid button:disabled :deep(path),
.shape-picker__grid button:disabled :deep(rect),
.shape-picker__grid button:disabled :deep(polygon),
.shape-picker__grid button:disabled :deep(circle),
.shape-picker__grid button:disabled :deep(ellipse) { fill: #cfceca !important; stroke: #8f8e89 !important; stroke-width: var(--shape-stroke, 2.4px) !important; vector-effect: non-scaling-stroke; }
.shape-picker [data-shape-group="engraved-tiles"] button,
.shape-picker [data-shape-group="printed-tiles"] button,
.shape-picker [data-shape-group="3d-blocks"] button { overflow: hidden; }
.shape-picker [data-shape-group="engraved-tiles"] button::after,
.shape-picker [data-shape-group="printed-tiles"] button::after,
.shape-picker [data-shape-group="3d-blocks"] button:not(:disabled)::after { content: ''; position: absolute; z-index: 2; inset: 0; border: 1.6px solid #8f8e89; pointer-events: none; }
.shape-picker [data-shape-group="engraved-tiles"] button:not(:disabled):not(.active):hover,
.shape-picker [data-shape-group="printed-tiles"] button:not(:disabled):not(.active):hover { background: #dfe4e5; }
.shape-picker [data-shape-group="engraved-tiles"] button:not(:disabled):not(.active):hover::after,
.shape-picker [data-shape-group="printed-tiles"] button:not(:disabled):not(.active):hover::after { border-color: #b8c6ce; }
.shape-picker [data-shape-group="engraved-tiles"] button.active,
.shape-picker [data-shape-group="printed-tiles"] button.active { background: #ccd5da; }
.shape-picker [data-shape-group="engraved-tiles"] button.active::after,
.shape-picker [data-shape-group="printed-tiles"] button.active::after { border-color: #2f365d; }
.shape-picker [data-shape-group="engraved-tiles"] button:disabled,
.shape-picker [data-shape-group="printed-tiles"] button:disabled,
.shape-picker .shape-picker__group[data-shape-group="3d-blocks"] button:disabled { background: transparent; }
.shape-picker [data-shape-group="engraved-tiles"] button :deep(svg),
.shape-picker [data-shape-group="printed-tiles"] button :deep(svg),
.shape-picker .shape-picker__group[data-shape-group="3d-blocks"] button :deep(svg) { position: absolute; inset: 0; display: block; width: 100% !important; height: 100% !important; max-width: 100%; max-height: 100%; }
.shape-picker [data-shape-group="engraved-tiles"] button:not(.active) :deep(path),
.shape-picker [data-shape-group="engraved-tiles"] button:not(.active) :deep(rect),
.shape-picker [data-shape-group="engraved-tiles"] button:not(.active) :deep(polygon),
.shape-picker [data-shape-group="engraved-tiles"] button:not(.active) :deep(circle),
.shape-picker [data-shape-group="engraved-tiles"] button:not(.active) :deep(ellipse) { fill: transparent !important; stroke: #8f8e89 !important; }
.shape-picker [data-shape-group="engraved-tiles"] button:not(:disabled):not(.active):hover :deep(path),
.shape-picker [data-shape-group="engraved-tiles"] button:not(:disabled):not(.active):hover :deep(rect),
.shape-picker [data-shape-group="engraved-tiles"] button:not(:disabled):not(.active):hover :deep(polygon),
.shape-picker [data-shape-group="engraved-tiles"] button:not(:disabled):not(.active):hover :deep(circle),
.shape-picker [data-shape-group="engraved-tiles"] button:not(:disabled):not(.active):hover :deep(ellipse) { fill: transparent !important; stroke: #b8c6ce !important; }
.shape-picker [data-shape-group="engraved-tiles"] button.active :deep(path),
.shape-picker [data-shape-group="engraved-tiles"] button.active :deep(rect),
.shape-picker [data-shape-group="engraved-tiles"] button.active :deep(polygon),
.shape-picker [data-shape-group="engraved-tiles"] button.active :deep(circle),
.shape-picker [data-shape-group="engraved-tiles"] button.active :deep(ellipse) { fill: transparent !important; stroke: #2f365d !important; }
.shape-picker [data-shape-id^="Aspen"]:not(.active) :deep(path),
.shape-picker [data-shape-id^="Bamboo"]:not(.active) :deep(path),
.shape-picker [data-shape-menu-id^="Aspen"]:not(.active) :deep(path),
.shape-picker [data-shape-menu-id^="Bamboo"]:not(.active) :deep(path) { fill: #8f8e89 !important; stroke: none !important; }
.shape-picker [data-shape-id^="Aspen"]:not(:disabled):not(.active):hover :deep(path),
.shape-picker [data-shape-id^="Bamboo"]:not(:disabled):not(.active):hover :deep(path),
.shape-picker [data-shape-menu-id^="Aspen"]:not(:disabled):not(.active):hover :deep(path),
.shape-picker [data-shape-menu-id^="Bamboo"]:not(:disabled):not(.active):hover :deep(path) { fill: #b8c6ce !important; stroke: none !important; }
.shape-picker [data-shape-id^="Aspen"].active :deep(path),
.shape-picker [data-shape-id^="Bamboo"].active :deep(path),
.shape-picker [data-shape-menu-id^="Aspen"].active :deep(path),
.shape-picker [data-shape-menu-id^="Bamboo"].active :deep(path) { fill: #2f365d !important; stroke: none !important; }
.shape-picker [data-shape-id^="Geo"] :deep(path),
.shape-picker [data-shape-id^="Pinstripe"] :deep(path),
.shape-picker [data-shape-id^="Blossom"] :deep(path),
.shape-picker [data-shape-id^="Strand"] :deep(path),
.shape-picker [data-shape-menu-id^="Geo"] :deep(path),
.shape-picker [data-shape-menu-id^="Pinstripe"] :deep(path),
.shape-picker [data-shape-menu-id^="Blossom"] :deep(path),
.shape-picker [data-shape-menu-id^="Strand"] :deep(path) { fill: transparent !important; stroke: #8f8e89 !important; stroke-width: .4px !important; vector-effect: non-scaling-stroke; }
.shape-picker [data-shape-id^="Geo"]:not(:disabled):not(.active):hover :deep(path),
.shape-picker [data-shape-id^="Pinstripe"]:not(:disabled):not(.active):hover :deep(path),
.shape-picker [data-shape-id^="Blossom"]:not(:disabled):not(.active):hover :deep(path),
.shape-picker [data-shape-id^="Strand"]:not(:disabled):not(.active):hover :deep(path),
.shape-picker [data-shape-menu-id^="Geo"]:not(:disabled):not(.active):hover :deep(path),
.shape-picker [data-shape-menu-id^="Pinstripe"]:not(:disabled):not(.active):hover :deep(path),
.shape-picker [data-shape-menu-id^="Blossom"]:not(:disabled):not(.active):hover :deep(path),
.shape-picker [data-shape-menu-id^="Strand"]:not(:disabled):not(.active):hover :deep(path) { fill: transparent !important; stroke: #b8c6ce !important; }
.shape-picker [data-shape-id^="Geo"].active :deep(path),
.shape-picker [data-shape-id^="Pinstripe"].active :deep(path),
.shape-picker [data-shape-id^="Blossom"].active :deep(path),
.shape-picker [data-shape-id^="Strand"].active :deep(path),
.shape-picker [data-shape-menu-id^="Geo"].active :deep(path),
.shape-picker [data-shape-menu-id^="Pinstripe"].active :deep(path),
.shape-picker [data-shape-menu-id^="Blossom"].active :deep(path),
.shape-picker [data-shape-menu-id^="Strand"].active :deep(path) { fill: transparent !important; stroke: #2f365d !important; stroke-width: .4px !important; vector-effect: non-scaling-stroke; }
.shape-picker [data-shape-id^="Geo"]:disabled :deep(path),
.shape-picker [data-shape-id^="Pinstripe"]:disabled :deep(path),
.shape-picker [data-shape-id^="Blossom"]:disabled :deep(path),
.shape-picker [data-shape-id^="Strand"]:disabled :deep(path) { fill: transparent !important; stroke: #8f8e89 !important; stroke-width: .4px !important; vector-effect: non-scaling-stroke; }
.shape-picker .shape-picker__grid--unavailable.shape-picker__group[data-shape-group="3d-blocks"] button { background: transparent; }
.shape-picker [data-block-preview]::after { border-color: #8f8e89; }
.shape-picker [data-block-preview]:disabled::after { display: none; }
.shape-picker .shape-picker__group[data-shape-group="3d-blocks"] button :deep(path),
.shape-picker .shape-picker__group[data-shape-group="3d-blocks"] button :deep(rect),
.shape-picker .shape-picker__group[data-shape-group="3d-blocks"] button :deep(polygon),
.shape-picker .shape-picker__group[data-shape-group="3d-blocks"] button :deep(circle),
.shape-picker .shape-picker__group[data-shape-group="3d-blocks"] button :deep(ellipse) { fill: transparent !important; stroke: #8f8e89 !important; stroke-width: var(--shape-stroke, 2.4px) !important; }
.shape-picker .shape-picker__group[data-shape-group="3d-blocks"] button[data-block-preview] :deep(path),
.shape-picker .shape-picker__group[data-shape-group="3d-blocks"] button[data-block-preview] :deep(rect),
.shape-picker .shape-picker__group[data-shape-group="3d-blocks"] button[data-block-preview] :deep(polygon),
.shape-picker .shape-picker__group[data-shape-group="3d-blocks"] button[data-block-preview] :deep(circle),
.shape-picker .shape-picker__group[data-shape-group="3d-blocks"] button[data-block-preview] :deep(ellipse) { stroke-width: .5px !important; vector-effect: non-scaling-stroke; }
.shape-picker [data-shape-group="3d-engraved"] button { background: transparent; cursor: not-allowed; }
.shape-picker__other-label { margin: 21px 0 13px; padding-top: 17px; border-top: 1px dashed #e4e3de; color: #a8a7a1; font-size: 11.5px; font-weight: 600; }
.shape-picker__grid-link { padding: 0; border: 0; background: none; color: #313558; font: inherit; text-decoration: underline; cursor: pointer; font-weight: 700; }
.shape-picker__grid-link:focus-visible { outline: 2px solid #313558; outline-offset: 2px; }
.shape-picker--3d > header { margin-bottom: 4px; }
.shape-picker--3d .shape-picker__other-label { margin: 0 0 10px; padding-top: 6px; }
.shape-picker--3d .shape-picker__group-heading { margin: 20px 0 4px; }
.shape-picker .shape-picker__group-heading + .shape-picker__other-label { margin: 0 0 10px !important; margin-top: 0 !important; padding-top: 6px !important; }
.shape-picker .shape-picker__group-heading:has(+ .shape-picker__other-label) { margin-bottom: 0 !important; }
.shape-picker--square > .shape-picker__other-label:first-of-type { margin-top: 19px; }
.shape-picker--square > .shape-picker__other-label:last-of-type { margin-top: 19px; }
.shape-picker__children { position: absolute; z-index: 100; top: calc(100% + 8px); left: 0; display: grid; grid-template-columns: 1fr; gap: 8px; width: max-content; min-width: 64px; min-height: 64px; padding: 12px 20px; justify-items: center; align-items: center; border: 1px solid #5dcbf4; border-radius: 4px; background: #fff; box-shadow: 0 3px 10px rgb(28 28 30 / 14%); }
.shape-picker--45-square .shape-picker__children { padding: 20px 28px 32px; }
.shape-picker__children > button:first-child { margin-bottom: 4px; }
.shape-picker__children--right { right: 0; left: auto; }
.shape-picker__grid :deep(svg) { overflow: visible; pointer-events: none; }
.shape-picker--45-square .shape-picker__grid :deep(svg) { max-width: 100%; max-height: 100%; overflow: hidden; }
.shape-picker--45-square [data-shape-group="engraved-tiles"] .shape-picker__grid { column-gap: 24px; row-gap: 10px; }
.shape-picker--45-square [data-shape-group="engraved-tiles"] button { overflow: visible; background: transparent !important; }
.shape-picker--45-square [data-shape-group="engraved-tiles"] button::after { display: none; }
.shape-picker--45-square [data-shape-group="engraved-tiles"] button :deep(svg) { overflow: visible; }
.shape-picker--45-square [data-shape-group="engraved-tiles"] button :deep(path:first-child) { fill: transparent !important; }
.shape-picker--45-square [data-shape-group="engraved-tiles"] button:not(:disabled):not(.active):hover :deep(path:first-child) { fill: #dfe4e5 !important; }
.shape-picker--45-square [data-shape-group="printed-tiles"] .shape-picker__grid { gap: 30px !important; padding: 15px 0; padding-left: 12px; }
.shape-picker--45-square [data-shape-group="printed-tiles"] button { transform: rotate(45deg); }
.shape-picker--45-square [data-shape-group="printed-tiles"] button :deep(svg) { overflow: visible; }
.shape-picker--45-square [data-shape-group="engraved-tiles"] button.active :deep(path:first-child) { fill: #ccd5da !important; }
.shape-picker--square.shape-picker--admin .shape-picker__item:has([data-shape-id="MiniShiplapVertical"]) { margin-left: 22px; }
.shape-picker--square .shape-picker__item:has([data-shape-id="MiniShiplapVertical"]) .shape-picker__children { right: 0; left: auto; }
.shape-picker--square.shape-picker--admin .shape-picker__item:has([data-shape-id="MiniShiplapVertical"]) .shape-picker__children { right: auto; left: 50%; transform: translateX(-50%); }
.shape-picker--square.shape-picker--admin .shape-picker__item:has([data-shape-id="ShiplapVertical"]) .shape-picker__children,
.shape-picker--square.shape-picker--admin .shape-picker__item:has([data-shape-id="HalflapVertical"]) .shape-picker__children { left: -20px; }
</style>

<style scoped>
.shape-picker__section { display: contents; }
.shape-picker__engraved-panel {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #000;
  padding: 8px 6px;
  margin-top: 0;
}
.shape-picker__group:has(> .shape-picker__engraved-panel) { gap: 0 !important; }
.shape-picker__layered-guidance { margin: 0; font-size: 12.5px; line-height: 1.3; }
.shape-picker .shape-picker__engraved-panel .shape-picker__grid { margin: 0; }
.shape-picker:not(.shape-picker--45-square) .shape-picker__engraved-panel .shape-picker__grid { justify-content: space-between; gap: 0 !important; }
.shape-picker .shape-picker__engraved-panel .shape-picker__grid + .shape-picker__grid { margin-top: 10px; }
.shape-picker--45-square .shape-picker__engraved-panel [data-shape-root] { width: 70.71px !important; height: 70.71px !important; }
.shape-picker--45-square .shape-picker__engraved-panel .shape-picker__grid { padding-left: 10px; box-sizing: border-box; }
.shape-picker--45-square .shape-picker__engraved-9mm { width: 50px; height: 50px; transform: rotate(45deg); transform-origin: center; }
.shape-picker--45-square .shape-picker__layered-panel [data-shape-root] { width: 70.71px !important; height: 70.71px !important; overflow: visible; }
.shape-picker--45-square .shape-picker__layered-panel [data-shape-root] .shape-picker__layered-icon { width: 50px !important; height: 50px !important; transform: rotate(45deg); transform-origin: center; }
.shape-picker--45-square .shape-picker__children .shape-picker__layered-icon { width: 50px !important; height: 50px !important; transform: rotate(45deg); transform-origin: center; }
.shape-picker--45-square .shape-picker__layered-panel .shape-picker__grid { row-gap: 10px; padding-left: 10px; box-sizing: border-box; }
.shape-picker--45-square .shape-picker__layered-panel .shape-picker__grid + .shape-picker__grid { margin-top: 10px; }
.shape-picker__layered-icon { max-width: none; }
/* These SVGs include their own silhouette and extend below the nominal tile face. */
.shape-picker [data-shape-group="engraved-tiles"] button:has(> .shape-picker__layered-icon) { overflow: visible; background: transparent !important; }
.shape-picker [data-shape-group="engraved-tiles"] button:has(> .shape-picker__layered-icon)::after { display: none; }
/* Figma state exports recolor the engraving and bevel, not the button rectangle. */
.shape-picker button:has(> .shape-picker__layered-icon):is(:hover, :focus-visible, .active, :disabled) :deep([data-icon-state="default"]) { visibility: hidden; }
.shape-picker button:not(:disabled):not(.active):is(:hover, :focus-visible) :deep([data-icon-state="hover"]),
.shape-picker button.active:not(:disabled) :deep([data-icon-state="selected"]),
.shape-picker button:disabled :deep([data-icon-state="disabled"]) { visibility: visible; }
.shape-picker__engraved-depth { grid-column: 1 / -1; margin: 10px 0 0; }
.shape-picker__engraved-depth p { margin: 0 0 2px; font-size: 12.5px; line-height: 19px; }
.shape-picker__engraved-depth > div { display: flex; gap: 5px; }
.shape-picker__engraved-depth button { display: flex; align-items: center; justify-content: space-between; flex: 1; width: auto !important; height: 36px !important; border: 1px solid #000; border-bottom: 0; border-radius: 0; font-size: 17px; font-weight: 800; text-align: left; padding: 0 12px; color: var(--studio-navy, #313558); background: white !important; }
.shape-picker__engraved-depth button.active { background: var(--studio-navy, #313558) !important; color: white; }
.shape-picker__engraved-depth button::after { display: none; }
</style>
